import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ViewSourceLink } from '@/components/accounting/ViewSourceLink';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { toast } from 'sonner';
import { ArrowRight, Network, TrendingUp, TrendingDown, Download, Wallet, History, Info } from 'lucide-react';
import api from '@/lib/api';
import { exportInternalStatementToPDF } from '@/lib/pdfExport';
import { SettleAllButton } from './finance/SettleAllButton';
import MonthlyInterBranchView from '@/components/finance/MonthlyInterBranchView';
import { localToday, toLocalYmd } from '@/lib/dateRange';
import { useWebSocket } from '@/hooks/useWebSocket';

const fmtSR = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayISO = () => localToday();

export default function InterBranchLedgerPage() {
  const navigate = useNavigate();
  const _cameFromBranch = new URLSearchParams(window.location.search).get('branch');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: 'all' });
  const [branchFilter, setBranchFilter] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('branch');
    return p ? [p] : [];
  });
  const [branches, setBranches] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [platformDues, setPlatformDues] = useState({ items: [], open_total: 0 });
  const [clearingAll, setClearingAll] = useState(false);
  // Settlement-prompt state for the platform-due "Settle" button. Holds
  // the picked due + the user's payment-method choice (bank/cash) and
  // the bank-account picker (required when method=bank).
  const [pdSettleOpen, setPdSettleOpen] = useState(false);
  const [pdSettleData, setPdSettleData] = useState({ due: null, payment_method: 'bank', bank_account_id: '', notes: '', reference: '' });
  const [bankAccountOptions, setBankAccountOptions] = useState([]);

  // Settle dialog state
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleForm, setSettleForm] = useState({
    from_branch_id: '',
    to_branch_id: '',
    from_branch: '',
    to_branch: '',
    amount: '',
    payment_mode: 'cash',
    date: todayISO(),
    notes: '',
  });
  const [posting, setPosting] = useState(false);
  // Multi-pair "step through" settlement queue. When a branch owes several
  // branches, load every unsettled pair and walk them one dialog at a time.
  const [settleQueue, setSettleQueue] = useState([]);   // array of netted rows
  const [settleQueueIdx, setSettleQueueIdx] = useState(0);

  const loadPairIntoForm = (n) => {
    setSettleForm({
      from_branch_id: n.from_id,
      to_branch_id: n.to_id,
      from_branch: n.from_branch,
      to_branch: n.to_branch,
      amount: String(n.owes || ''),
      payment_mode: 'cash',
      date: todayISO(),
      notes: `Settle ${n.from_branch} → ${n.to_branch}`,
    });
  };

  const startStepSettle = () => {
    const queue = (data?.netted_balances || []).filter(n => Number(n.owes) > 0);
    if (!queue.length) { toast.info('No outstanding pairs to settle'); return; }
    setSettleQueue(queue);
    setSettleQueueIdx(0);
    loadPairIntoForm(queue[0]);
    setSettleOpen(true);
  };

  const advanceQueue = () => {
    const next = settleQueueIdx + 1;
    if (next < settleQueue.length) {
      setSettleQueueIdx(next);
      loadPairIntoForm(settleQueue[next]);
    } else {
      setSettleQueue([]);
      setSettleQueueIdx(0);
      setSettleOpen(false);
      toast.success('All pairs stepped through');
      fetchData();
    }
  };

  // Deep-link handlers:
  //  • ?branch=<id>&settle=1        → open Settle dialog pre-filled for that branch
  //  • ?settle=1&from=&to=&amt=     → auto-direction + exact amount (one-tap from
  //                                   the branch-card chip, debtor→creditor)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('settle') === '1' && branches.length && !settleOpen) {
      const fromId = sp.get('from');
      const toId = sp.get('to');
      const amt = sp.get('amt');
      if (fromId && toId) {
        const fb = branches.find((x) => x.id === fromId);
        const tb = branches.find((x) => x.id === toId);
        setSettleForm((f) => ({
          ...f,
          from_branch_id: fromId, from_branch: fb?.name || '',
          to_branch_id: toId, to_branch: tb?.name || '',
          amount: amt ? String(Math.abs(Number(amt))) : f.amount,
        }));
        setSettleOpen(true);
      } else {
        const bid = sp.get('branch');
        const b = branches.find((x) => x.id === bid);
        if (b) {
          setSettleForm((f) => ({ ...f, from_branch_id: b.id, from_branch: b.name }));
          setSettleOpen(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches]);

  // Statement (per-pair running balance) dialog state
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [stmtData, setStmtData] = useState(null);

  // iter938 — Internal Settlement Statement (non-revenue stock transfers)
  const [internalMonth, setInternalMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [internalStmt, setInternalStmt] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);

  const fetchInternalStatement = async (m) => {
    setInternalLoading(true);
    try {
      const r = await api.get('/reports/inter-branch/internal-statement', { params: { month: m || internalMonth } });
      setInternalStmt(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load internal statement');
    } finally { setInternalLoading(false); }
  };

  useEffect(() => { fetchInternalStatement(internalMonth); }, [internalMonth]);

  // Record Settlement from a statement: receiver (to_branch) pays sender (from_branch).
  const openSettleFromStatement = (stmt) => {
    setSettleForm({
      from_branch_id: stmt.to_branch_id,     // debtor (receiver) — funds OUT
      to_branch_id: stmt.from_branch_id,     // creditor (sender) — funds IN
      from_branch: stmt.to_branch_name,
      to_branch: stmt.from_branch_name,
      amount: String(stmt.outstanding || stmt.total_value || ''),
      payment_mode: 'cash',
      date: todayISO(),
      notes: `Internal settlement: ${stmt.to_branch_name} → ${stmt.from_branch_name} (stock transfers)`,
    });
    setSettleOpen(true);
  };

  const openStatement = async (pair) => {
    setStmtOpen(true);
    setStmtLoading(true);
    setStmtData(null);
    try {
      const r = await api.get('/reports/inter-branch/statement', {
        params: { branch_a: pair.from_id, branch_b: pair.to_id },
      });
      setStmtData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load statement');
      setStmtOpen(false);
    } finally { setStmtLoading(false); }
  };

  const reverseSettlement = async (st) => {
    if (!window.confirm(`Reverse this settlement?\n\n${st.from_branch_name} → ${st.to_branch_name}\n${cur()} ${Number(st.amount || 0).toLocaleString()}\n\nThe balance will be restored.`)) return;
    try {
      await api.delete(`/reports/inter-branch/settle/${st.id}`);
      toast.success('Settlement reversed');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reverse');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      if (branchFilter.length === 1) params.branch_id = branchFilter[0];
      else if (branchFilter.length > 1) params.branch_ids = branchFilter.join(',');
      const [r, s, pd, b] = await Promise.all([
        api.get('/reports/inter-branch', { params }),
        api.get('/reports/inter-branch/settlements', { params }),
        api.get('/platform-payment-dues').catch(() => ({ data: { items: [], open_total: 0 } })),
        branches.length === 0
          ? api.get('/branches').catch(() => ({ data: [] }))
          : Promise.resolve({ data: branches }),
      ]);
      setData(r.data);
      setSettlements(s.data?.settlements || []);
      setPlatformDues(pd.data || { items: [], open_total: 0 });
      if (branches.length === 0) {
        setBranches(Array.isArray(b.data) ? b.data : (b.data?.branches || []));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inter-branch report');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [dateFilter.start, dateFilter.end, branchFilter]);

  // Live Bridge: refresh instantly when any cross-branch txn / settlement
  // fires a `balance_updated` event over the dashboard WebSocket.
  useWebSocket((msg) => {
    if (msg?.type === 'balance_updated') {
      fetchData();
      fetchInternalStatement(internalMonth);
    }
  });

  const exportCSV = () => {
    if (!data?.transactions) return;
    // iter500 — Include branch filter + period in the filename so a
    // user exporting "Q2 · Riyadh Branch" doesn't lose context.
    const branchLabel = branchFilter.length > 0
      ? `_${branchFilter.map(id => branches.find(b => b.id === id)?.name || id).join('-').replace(/\s+/g, '-')}`
      : '';
    const rows = [
      ['Date', 'Payer Branch', 'Beneficiary Branch', 'Amount', 'Category', 'Description', 'Mode'],
      ...data.transactions.map(t => [t.date, t.payer_branch, t.beneficiary_branch, t.amount, t.category, t.description, t.payment_mode]),
    ];
    const csv = rows.map(r => r.map(c => {
      const s = String(c ?? '');
      return s.includes(',') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `inter-branch${branchLabel}-${data.period?.start_date}_to_${data.period?.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const openSettleFromRow = (n) => {
    setSettleForm({
      from_branch_id: n.from_id,
      to_branch_id: n.to_id,
      from_branch: n.from_branch,
      to_branch: n.to_branch,
      amount: String(n.owes || ''),
      payment_mode: 'cash',
      date: todayISO(),
      notes: `Settle ${n.from_branch} → ${n.to_branch}`,
    });
    setSettleOpen(true);
  };

  const postSettlement = async () => {
    if (!settleForm.from_branch_id || !settleForm.to_branch_id) {
      toast.error('Both branches are required');
      return;
    }
    const amt = Number(settleForm.amount);
    if (!amt || amt <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    setPosting(true);
    try {
      const body = {
        from_branch_id: settleForm.from_branch_id,
        to_branch_id: settleForm.to_branch_id,
        amount: amt,
        payment_mode: settleForm.payment_mode,
        date: settleForm.date,
        notes: settleForm.notes,
      };
      const r = await api.post('/reports/inter-branch/settle', body);
      toast.success(r.data?.message || 'Settlement posted');
      if (settleQueue.length) {
        // Queue mode: advance to the next unsettled pair (keeps dialog open).
        advanceQueue();
      } else {
        setSettleOpen(false);
        fetchData();
      }
      fetchInternalStatement(internalMonth);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post settlement');
    } finally { setPosting(false); }
  };

  const settlePlatformDue = (due) => {
    if (!due?.id) return;
    setPdSettleData({
      due,
      payment_method: 'bank',
      bank_account_id: '',
      notes: `Settled ${due.owing_branch_name} → ${due.owed_to_branch_name} (platform: ${due.platform_name})`,
      reference: '',
    });
    // Lazy-load bank accounts the first time the dialog opens
    if (bankAccountOptions.length === 0) {
      api.get('/bank-accounts?active_only=true')
        .then(r => setBankAccountOptions(Array.isArray(r.data) ? r.data : (r.data?.accounts || [])))
        .catch(() => setBankAccountOptions([]));
    }
    setPdSettleOpen(true);
  };

  const submitPlatformDueSettle = async () => {
    const { due, payment_method, bank_account_id, notes, reference } = pdSettleData;
    if (!due?.id) return;
    if (payment_method === 'bank' && !bank_account_id) {
      toast.error('Pick the bank account that received the cash');
      return;
    }
    try {
      await api.post(`/platform-payment-dues/${due.id}/settle`, {
        payment_method,
        bank_account_id: payment_method === 'bank' ? bank_account_id : null,
        notes,
        settlement_reference: reference,
      });
      toast.success(`Platform due settled by ${payment_method}`);
      setPdSettleOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to settle');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="inter-branch-ledger-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">
              <Network size={22} className="text-white" />
            </div>
            <div>
              {_cameFromBranch && (
                <button
                  type="button"
                  onClick={() => navigate('/branches')}
                  className="text-xs text-muted-foreground hover:text-primary mb-1 inline-flex items-center gap-1"
                  data-testid="ledger-back-to-branches"
                >← Back to branches</button>
              )}
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Inter-Branch Ledger</h1>
              <p className="text-sm text-muted-foreground mt-0.5">When one branch pays expenses on behalf of another — track who owes whom</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <DateQuickFilter onFilterChange={setDateFilter} />
            <BranchMultiSelect value={branchFilter} onChange={setBranchFilter} branches={branches} width="w-[180px]" testid="branch-filter-select" />
            <SettleAllButton
              netted={data?.netted_balances || []}
              dateFilter={dateFilter}
              onDone={fetchData}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={clearingAll || !(data?.netted_balances?.length)}
              onClick={async () => {
                if (!window.confirm('Run the All-Time Clearing House?\n\nThis nets out EVERY open inter-branch balance across all branches using "Internal Offset" settlements. It does NOT move Cash or Bank — it only zeroes the inter-branch ledger.')) return;
                setClearingAll(true);
                try {
                  const r = await api.post('/reports/inter-branch/clear-all');
                  toast.success(r.data?.message || 'Inter-branch balances cleared');
                  fetchData();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to clear balances');
                } finally {
                  setClearingAll(false);
                }
              }}
              className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400"
              data-testid="clear-all-internal-offset-btn"
              title="Net out all inter-branch debts via Internal Offset (no cash/bank movement)"
            >
              <Network size={14} className="mr-1" /> {clearingAll ? 'Clearing…' : 'Clear All (Offset)'}
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} disabled={!data?.transactions?.length} data-testid="export-btn">
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="border-border"><CardContent className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Cross-Branch Volume</p>
            <p className="text-2xl font-bold text-foreground tabular-nums mt-1" data-testid="total-volume">{fmtSR(data?.total_cross_branch_volume || 0)}</p>
          </CardContent></Card>
          <Card className="border-border"><CardContent className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Transactions</p>
            <p className="text-2xl font-bold text-foreground tabular-nums mt-1" data-testid="total-tx">{data?.transaction_count || 0}</p>
          </CardContent></Card>
          <Card className="border-border"><CardContent className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Branch Pairs with Net Balance</p>
            <p className="text-2xl font-bold text-foreground tabular-nums mt-1" data-testid="net-pairs">{data?.netted_balances?.length || 0}</p>
          </CardContent></Card>
        </div>

        {/* Netted Balances */}
        <Card data-testid="netted-card"><CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-sm font-bold text-foreground">Net Balances (settlement view)</h3>
            {(data?.netted_balances?.filter(n => Number(n.owes) > 0).length > 1) && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs border-indigo-400/40 text-indigo-700 hover:bg-indigo-50 dark:text-indigo-400"
                onClick={startStepSettle}
                data-testid="step-settle-btn"
                title="Step through every outstanding pair one dialog at a time"
              >
                <Wallet size={12} className="mr-1" /> Step-Settle All ({data.netted_balances.filter(n => Number(n.owes) > 0).length})
              </Button>
            )}
          </div>
          {data?.by_mode && (
            <p className="text-[11px] text-muted-foreground mb-3" data-testid="ibl-mode-split">
              Cash: <span className="font-semibold text-emerald-600">{fmtSR(data.by_mode.cash || 0)}</span>
              <span className="mx-2">·</span>
              Bank: <span className="font-semibold text-sky-600">{fmtSR(data.by_mode.bank || 0)}</span>
              {(data.by_mode.other || 0) > 0 && <><span className="mx-2">·</span>Other: <span className="font-semibold">{fmtSR(data.by_mode.other)}</span></>}
            </p>
          )}
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : data?.netted_balances?.length ? (
            <div className="space-y-2">
              {data.netted_balances.map((n, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-gradient-to-r from-warning/5 to-transparent" data-testid={`net-row-${i}`}>
                  <Badge variant="outline" className="font-semibold text-xs bg-error/10 text-error border-error/30">{n.from_branch}</Badge>
                  <span className="text-xs text-muted-foreground">owes</span>
                  <ArrowRight size={14} className="text-muted-foreground" />
                  <Badge variant="outline" className="font-semibold text-xs bg-success/10 text-success border-success/30">{n.to_branch}</Badge>
                  <span className="ml-auto text-lg font-bold text-warning tabular-nums">{fmtSR(n.owes)}</span>
                  <span className="text-[10px] text-muted-foreground">({n.transaction_count} tx)</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    onClick={() => openStatement(n)}
                    data-testid={`stmt-btn-${i}`}
                  >
                    <History size={12} className="mr-1" /> Statement
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    className="h-7 px-2 text-xs ml-1"
                    onClick={() => openSettleFromRow(n)}
                    data-testid={`settle-btn-${i}`}
                  >
                    <Wallet size={12} className="mr-1" /> Settle
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-sm text-muted-foreground">No inter-branch activity in this period</div>
          )}
        </CardContent></Card>

        {/* iter706 — Monthly view: pair statement + branch-vs-branch
            matrix with opening / activity / closing breakdown. Auto-
            nets opposite balances per pair, per month. Cash transfers
            count as settlements (user choice in iter706 plan). */}
        <MonthlyInterBranchView branches={branches} />

        {/* iter938 — INTERNAL Settlement Statement (non-revenue stock transfers). */}
        <Card className="border-orange-200/60 dark:border-orange-900/40" data-testid="internal-statement-panel">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-outfit font-semibold flex items-center gap-2">
                  <Network size={18} className="text-orange-500" /> Internal Settlement Statement
                </h3>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide border-orange-400/50 text-orange-600 dark:text-orange-400" data-testid="internal-not-tax-invoice">
                    INTERNAL — NOT A TAX INVOICE
                  </Badge>
                  <span className="text-[11px] text-muted-foreground">Excluded from Sales / VAT reports</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Month</Label>
                <Input type="month" value={internalMonth} onChange={e => setInternalMonth(e.target.value)} className="h-9 w-40" data-testid="internal-month-input" />
              </div>
            </div>

            {internalLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            ) : !internalStmt?.statements?.length ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="internal-empty">No inter-branch stock transfers for this month.</p>
            ) : (
              <div className="space-y-4">
                <div className="text-xs text-muted-foreground">
                  Total transferred this month: <span className="font-semibold text-foreground">{cur(internalStmt.total_transferred)}</span>
                </div>
                {internalStmt.statements.map((s, idx) => (
                  <div key={idx} className="rounded-xl border border-border/60 overflow-hidden" data-testid={`internal-statement-${idx}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/40 px-4 py-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <span>{s.from_branch_name}</span>
                        <ArrowRight size={14} className="text-muted-foreground" />
                        <span>{s.to_branch_name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground">Outstanding (net)</p>
                          <p className={`text-sm font-semibold ${s.outstanding > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-emerald-600'}`}>{cur(s.outstanding)}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => exportInternalStatementToPDF(s, {}, internalStmt?.period?.month || internalMonth)}
                          className="rounded-lg" data-testid={`internal-pdf-btn-${idx}`}>
                          <Download size={14} className="mr-1" /> PDF
                        </Button>
                        <Button size="sm" disabled={!(s.outstanding > 0)} onClick={() => openSettleFromStatement(s)}
                          className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white" data-testid={`record-settlement-btn-${idx}`}>
                          <Wallet size={14} className="mr-1" /> Record Settlement
                        </Button>
                      </div>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="text-muted-foreground border-b border-border/50">
                        <tr><th className="text-left p-2 font-medium">Date</th><th className="text-left p-2 font-medium">Description</th><th className="text-right p-2 font-medium">Value</th></tr>
                      </thead>
                      <tbody>
                        {s.lines.map((l, li) => (
                          <tr key={li} className="border-b border-border/30 last:border-0">
                            <td className="p-2 whitespace-nowrap">{l.date?.slice(0, 10)}</td>
                            <td className="p-2">{l.description}</td>
                            <td className="p-2 text-right font-mono">{cur(l.amount)}</td>
                          </tr>
                        ))}
                        <tr className="bg-muted/30 font-semibold">
                          <td className="p-2" colSpan={2}>Month Total</td>
                          <td className="p-2 text-right font-mono">{cur(s.total_value)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>


        {/* Platform-payment dues — auto-created when a delivery platform's
            cash lands in one branch's account but the sales were rung up
            across multiple branches. */}
        <Card data-testid="platform-dues-card"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-foreground">Online-Platform Inter-Branch Dues</h3>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
              {platformDues.items?.filter(d => d.status !== 'settled').length || 0} open
            </Badge>
          </div>
          {(!platformDues.items || platformDues.items.length === 0) ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No platform-payout dues yet. They'll appear here when you record a platform payment that landed in one branch but covers multiple branches' sales.
            </div>
          ) : (
            <div className="space-y-2">
              {platformDues.items.map((d, i) => (
                <div
                  key={d.id || i}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${d.status === 'settled' ? 'border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/10' : 'border-amber-200 bg-amber-50/40 dark:bg-amber-950/10'}`}
                  data-testid={`platform-due-row-${i}`}
                >
                  <Badge variant="outline" className="text-[10px] bg-stone-100 dark:bg-stone-800">
                    {d.platform_name}
                  </Badge>
                  <Badge variant="outline" className="font-semibold text-xs bg-error/10 text-error border-error/30">{d.owing_branch_name || '—'}</Badge>
                  <span className="text-xs text-muted-foreground">owes</span>
                  <ArrowRight size={14} className="text-muted-foreground" />
                  <Badge variant="outline" className="font-semibold text-xs bg-success/10 text-success border-success/30">{d.owed_to_branch_name || '—'}</Badge>
                  <span className="ml-auto text-base font-bold tabular-nums">{fmtSR(d.amount)}</span>
                  {d.status === 'settled' ? (
                    <Badge className="bg-emerald-500 text-white">Settled</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="default"
                      className="h-7 px-2 text-xs ml-1"
                      onClick={() => settlePlatformDue(d)}
                      data-testid={`platform-due-settle-${i}`}
                    >
                      <Wallet size={12} className="mr-1" /> Settle
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>

        {/* Per-Branch Summary */}
        {data?.branch_summary?.some(b => b.paid_for_others > 0 || b.paid_by_others > 0) && (
          <Card><CardContent className="p-4">
            <h3 className="text-sm font-bold text-foreground mb-3">Branch Activity Summary</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="branch-summary-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left p-2">Branch</th>
                    <th className="text-right p-2">Paid for Others</th>
                    <th className="text-right p-2">Paid by Others</th>
                    <th className="text-right p-2">Settled Out</th>
                    <th className="text-right p-2">Settled In</th>
                    <th className="text-right p-2">Gross Net</th>
                    <th className="text-right p-2">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {data.branch_summary.filter(b => b.paid_for_others > 0 || b.paid_by_others > 0 || b.settled_out > 0 || b.settled_in > 0).map(b => (
                    <tr key={b.branch_id} className="border-b border-border/30" data-testid={`branch-row-${b.branch_id}`}>
                      <td className="p-2 font-medium text-foreground">{b.branch_name}</td>
                      <td className="p-2 text-right tabular-nums text-success">{fmtSR(b.paid_for_others)}</td>
                      <td className="p-2 text-right tabular-nums text-error">{fmtSR(b.paid_by_others)}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{fmtSR(b.settled_out || 0)}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{fmtSR(b.settled_in || 0)}</td>
                      <td className={`p-2 text-right tabular-nums ${b.net > 0 ? 'text-success' : b.net < 0 ? 'text-error' : 'text-muted-foreground'}`}>{fmtSR(b.net)}</td>
                      <td className={`p-2 text-right tabular-nums font-bold ${(b.net_outstanding || 0) > 0 ? 'text-success' : (b.net_outstanding || 0) < 0 ? 'text-error' : 'text-muted-foreground'}`}>
                        {(b.net_outstanding || 0) > 0 ? <TrendingUp size={10} className="inline mr-0.5" /> : (b.net_outstanding || 0) < 0 ? <TrendingDown size={10} className="inline mr-0.5" /> : null}
                        {fmtSR(b.net_outstanding || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        )}

        {/* Past Settlements */}
        {settlements?.length > 0 && (
          <Card data-testid="settlements-card"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <History size={14} className="text-muted-foreground" />
              <h3 className="text-sm font-bold text-foreground">Past Settlements</h3>
              <Badge variant="outline" className="text-[10px]">{settlements.length}</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="settlements-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Payer</th>
                    <th className="text-left p-2">Payee</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-left p-2">Mode</th>
                    <th className="text-left p-2">Notes</th>
                    <th className="text-right p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map(st => (
                    <tr key={st.id} className="border-b border-border/30" data-testid={`settlement-row-${st.id}`}>
                      <td className="p-2 tabular-nums">{String(st.date).slice(0, 10)}</td>
                      <td className="p-2 font-medium">{st.from_branch_name}</td>
                      <td className="p-2">{st.to_branch_name}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{fmtSR(st.amount)}</td>
                      <td className="p-2"><Badge variant="outline" className="text-[10px] capitalize">{st.payment_mode}</Badge></td>
                      <td className="p-2 text-muted-foreground text-[11px] max-w-[280px] truncate">{st.notes}</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-error hover:bg-error/10" onClick={() => reverseSettlement(st)} data-testid={`reverse-settlement-${st.id}`}>
                          Undo
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        )}

        {/* All Transactions */}
        <Card><CardContent className="p-4">
          <h3 className="text-sm font-bold text-foreground mb-3">All Cross-Branch Transactions</h3>
          {data?.transactions?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="transactions-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Payer (on-behalf)</th>
                    <th className="text-left p-2">Beneficiary</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-left p-2">Category</th>
                    <th className="text-left p-2">Mode</th>
                    <th className="text-left p-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map(t => (
                    <tr key={t.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`tx-row-${t.id}`}>
                      <td className="p-2 tabular-nums">{String(t.date).slice(0, 10)}</td>
                      <td className="p-2 font-medium">{t.payer_branch}</td>
                      <td className="p-2">{t.beneficiary_branch}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{fmtSR(t.amount)}</td>
                      <td className="p-2 text-muted-foreground">{t.category}</td>
                      <td className="p-2 text-muted-foreground"><Badge variant="outline" className="text-[10px]">{t.payment_mode}</Badge></td>
                      <td className="p-2"><ViewSourceLink sourceType={t.source} sourceId={t.id} compact /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center" data-testid="ibl-empty-state">
              <div className="text-sm text-muted-foreground mb-3">
                No cross-branch transactions in this period.
              </div>
              <p className="text-xs text-muted-foreground max-w-md mx-auto mb-4">
                A cross-branch expense is one paid by Branch A but <em>for</em> Branch B
                (e.g. head-office paid the electricity for the Riyadh shop).
                Open <strong>Add Expense</strong>, set <em>Branch</em> to the payer,
                then enable <strong>&ldquo;For Another Branch&rdquo;</strong> and pick the beneficiary.
              </p>
              <a
                href="/expenses?action=add&cross_branch=1"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-info hover:bg-info/90 rounded-lg"
                data-testid="ibl-add-cross-branch-expense"
              >
                + Record Cross-Branch Expense
              </a>
            </div>
          )}
        </CardContent></Card>
      </div>

      {/* Settle Balance Dialog */}
      <Dialog open={settleOpen} onOpenChange={(o) => { setSettleOpen(o); if (!o) { setSettleQueue([]); setSettleQueueIdx(0); } }}>
        <DialogContent className="max-w-md" data-testid="settle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet size={16} className="text-indigo-500" /> Settle Inter-Branch Balance
            </DialogTitle>
            <DialogDescription>
              Posts a balanced Journal Entry that clears the debt between branches.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/70 dark:border-emerald-900 px-3 py-2" data-testid="settle-net-note">
            <Info size={14} className="text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-xs text-emerald-800 dark:text-emerald-300 leading-relaxed">
              Note: This action only moves cash; your Net Profit remains unchanged.
              <span className="block text-emerald-700/80 dark:text-emerald-400/80 mt-0.5" dir="rtl">ملاحظة: هذا الإجراء ينقل النقد فقط؛ صافي ربحك يبقى دون تغيير.</span>
            </p>
          </div>

          {settleQueue.length > 0 && (
            <div className="flex items-center justify-between rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200/60 dark:border-indigo-900 px-3 py-2" data-testid="settle-queue-progress">
              <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                Pair {settleQueueIdx + 1} of {settleQueue.length}
              </span>
              <button
                type="button"
                onClick={advanceQueue}
                disabled={posting}
                className="text-xs font-medium text-indigo-600 hover:underline disabled:opacity-50"
                data-testid="settle-skip-btn"
              >Skip →</button>
            </div>
          )}

          <div className="space-y-3 py-2">
            {/* From → To preview */}
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/40 border border-border">
              <Badge variant="outline" className="bg-error/10 text-error border-error/30 text-xs">{settleForm.from_branch || '—'}</Badge>
              <span className="text-xs text-muted-foreground">pays</span>
              <ArrowRight size={12} className="text-muted-foreground" />
              <Badge variant="outline" className="bg-success/10 text-success border-success/30 text-xs">{settleForm.to_branch || '—'}</Badge>
            </div>

            {/* Amount */}
            <div className="space-y-1">
              <Label className="text-xs">Amount (SR)</Label>
              <Input
                type="number"
                step="0.01"
                value={settleForm.amount}
                onChange={e => setSettleForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
                data-testid="settle-amount-input"
              />
            </div>

            {/* Payment Mode */}
            <div className="space-y-1">
              <Label className="text-xs">Payment Mode</Label>
              <Select value={settleForm.payment_mode} onValueChange={v => setSettleForm(f => ({ ...f, payment_mode: v }))}>
                <SelectTrigger data-testid="settle-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash" data-testid="mode-cash">Cash</SelectItem>
                  <SelectItem value="bank" data-testid="mode-bank">Bank</SelectItem>
                  <SelectItem value="other" data-testid="mode-other">Other / Clearing</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date (this month / custom) */}
            <div className="space-y-1">
              <Label className="text-xs">Settlement Date</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="date"
                  value={settleForm.date}
                  onChange={e => setSettleForm(f => ({ ...f, date: e.target.value }))}
                  data-testid="settle-date-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap text-xs"
                  onClick={() => {
                    const t = new Date();
                    const first = new Date(t.getFullYear(), t.getMonth(), 1);
                    setSettleForm(f => ({ ...f, date: toLocalYmd(first) }));
                  }}
                  data-testid="this-month-btn"
                >This month</Button>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-xs">Notes (optional)</Label>
              <Input
                value={settleForm.notes}
                onChange={e => setSettleForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Settlement reference"
                data-testid="settle-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSettleOpen(false); setSettleQueue([]); setSettleQueueIdx(0); }} disabled={posting} data-testid="settle-cancel-btn">Cancel</Button>
            <Button onClick={postSettlement} disabled={posting} data-testid="settle-confirm-btn">
              {posting ? 'Posting…' : settleQueue.length > 0 ? (settleQueueIdx + 1 < settleQueue.length ? 'Post & Next' : 'Post & Finish') : 'Post Settlement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Platform-due settlement dialog — pick bank vs cash */}
      <Dialog open={pdSettleOpen} onOpenChange={setPdSettleOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="pd-settle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wallet size={16} /> Settle Platform Due</DialogTitle>
            <DialogDescription>
              {pdSettleData.due ? (
                <>
                  <span className="font-semibold">{pdSettleData.due.owing_branch_name}</span> → <span className="font-semibold">{pdSettleData.due.owed_to_branch_name}</span>
                  <span className="ml-2 font-bold">{fmtSR(pdSettleData.due.amount)}</span>
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment Method *</Label>
              <Select
                value={pdSettleData.payment_method}
                onValueChange={(v) => setPdSettleData(d => ({ ...d, payment_method: v, bank_account_id: v === 'cash' ? '' : d.bank_account_id }))}
              >
                <SelectTrigger data-testid="pd-settle-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {pdSettleData.payment_method === 'bank' && (
              <div>
                <Label>Bank Account (received the cash) *</Label>
                <Select
                  value={pdSettleData.bank_account_id}
                  onValueChange={(v) => setPdSettleData(d => ({ ...d, bank_account_id: v }))}
                >
                  <SelectTrigger data-testid="pd-settle-bank-account"><SelectValue placeholder="Pick bank account..." /></SelectTrigger>
                  <SelectContent>
                    {bankAccountOptions.length === 0 ? (
                      <SelectItem value="__empty__" disabled>No bank accounts</SelectItem>
                    ) : bankAccountOptions.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name || b.account_name || b.bank_name} {b.account_number ? `· ${b.account_number}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Reference / Cheque #</Label>
              <Input
                placeholder="Optional"
                value={pdSettleData.reference}
                onChange={(e) => setPdSettleData(d => ({ ...d, reference: e.target.value }))}
                data-testid="pd-settle-reference"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={pdSettleData.notes}
                onChange={(e) => setPdSettleData(d => ({ ...d, notes: e.target.value }))}
                data-testid="pd-settle-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdSettleOpen(false)} data-testid="pd-settle-cancel-btn">Cancel</Button>
            <Button onClick={submitPlatformDueSettle} data-testid="pd-settle-confirm-btn">Confirm Settlement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inter-Branch Statement (per-pair running balance) */}
      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="max-w-3xl max-h-[85dvh] overflow-y-auto" data-testid="inter-branch-statement-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <History size={16} />
              {stmtData ? `Statement — ${stmtData.branch_a?.name} ↔ ${stmtData.branch_b?.name}` : 'Statement'}
            </DialogTitle>
            {stmtData?.since_last_settlement_on && (
              <DialogDescription>
                Showing transactions since last full settlement on <strong>{stmtData.since_last_settlement_on}</strong>.
                Earlier balance was fully cleared.
              </DialogDescription>
            )}
          </DialogHeader>

          {stmtLoading && <div className="p-6 text-center text-sm text-muted-foreground">Loading statement…</div>}

          {stmtData && (
            <div className="space-y-3">
              {/* Closing balance card */}
              <div className={`p-3 rounded-xl border-2 ${
                stmtData.closing_balance > 0.005 ? 'border-success/30 bg-success/5'
                : stmtData.closing_balance < -0.005 ? 'border-error/30 bg-error/5'
                : 'border-emerald-300 bg-emerald-50/40'
              }`}>
                {stmtData.currently_owes ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="font-semibold text-xs bg-error/10 text-error border-error/30">{stmtData.currently_owes.from}</Badge>
                    <span className="text-xs text-muted-foreground">owes</span>
                    <ArrowRight size={14} className="text-muted-foreground" />
                    <Badge variant="outline" className="font-semibold text-xs bg-success/10 text-success border-success/30">{stmtData.currently_owes.to}</Badge>
                    <span className="ml-auto text-xl font-bold tabular-nums">{fmtSR(stmtData.currently_owes.amount)}</span>
                  </div>
                ) : (
                  <div className="text-center text-sm font-semibold text-emerald-700">
                    ✓ Fully settled — no outstanding balance between these branches
                  </div>
                )}
              </div>

              {/* Running ledger */}
              {stmtData.rows?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="inter-branch-stmt-table">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Kind</th>
                        <th className="text-left p-2">Description</th>
                        <th className="text-right p-2">Δ</th>
                        <th className="text-right p-2">Running Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stmtData.rows.map((r, i) => (
                        <tr key={`${r.id || i}`} className="border-b border-border/30">
                          <td className="p-2 tabular-nums">{String(r.date).slice(0, 10)}</td>
                          <td className="p-2">
                            <Badge variant="outline" className={`text-[10px] capitalize ${r.kind === 'settlement' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}`}>
                              {r.kind}
                            </Badge>
                          </td>
                          <td className="p-2 max-w-[280px] truncate text-muted-foreground" title={r.description}>{r.description}</td>
                          <td className={`p-2 text-right tabular-nums ${r.delta > 0 ? 'text-success' : 'text-error'}`}>
                            {r.delta > 0 ? '+' : ''}{Number(r.delta).toFixed(2)}
                          </td>
                          <td className={`p-2 text-right tabular-nums font-semibold ${r.running_balance > 0.005 ? 'text-success' : r.running_balance < -0.005 ? 'text-error' : 'text-muted-foreground'}`}>
                            {fmtSR(r.running_balance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-6 text-center text-sm text-muted-foreground">No transactions between these branches</div>
              )}

              <p className="text-[10px] text-muted-foreground italic">
                +ve = {stmtData.branch_b?.name} owes {stmtData.branch_a?.name} · −ve = {stmtData.branch_a?.name} owes {stmtData.branch_b?.name}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStmtOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
